// Revenue and incoming deals on a map, for showing the book of business to
// people who do not live in the CRM.
//
// One read — the deals with a coordinate — drawn as two layers (see
// `mapDeals.ts`). Money is shown in the reader's report currency, converted at
// today's rate, because bubbles and totals cannot mix CAD and USD. Deals the map cannot draw
// are counted under it and linked to the deals list's location worklists.
//
// The map library is lazy-loaded: MapLibre is most of a megabyte, and no other
// screen needs it.

import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Suspense, lazy, useCallback, useMemo, type ReactNode } from "react";
import type { Currency, FxApplied, Partner, ReportScope } from "@crm/shared";
import styles from "./MapPage.module.css";
import {
  MAP_VIEWS,
  summariseMap,
  toMapDeals,
  type MapDeal,
  type MapSummary,
  type MapView,
} from "./mapDeals.js";
import { mapStateToSearch, searchToMapState, type MapSearch, type MapState } from "./mapSearch.js";
import { Card, CardHeader } from "../../design/Card.js";
import { Spinner } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { todayIsoDate, formatCalendarDate } from "../../format/calendarDate.js";
import { currencyView } from "../../format/displayCurrency.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { formatCount, formatMoney, formatRate } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";
import {
  MAP_DEAL_LIMIT,
  useFxRates,
  useMapDeals,
  useMapLocationGap,
  usePartnerOptions,
} from "../../api/queries.js";

const DealMap = lazy(async () => ({ default: (await import("./DealMap.js")).DealMap }));

const ANY_PARTNER = "__any__";

function viewLabel(view: MapView): string {
  switch (view) {
    case "both":
      return "Everything";
    case "revenue":
      return "Revenue";
    case "pipeline":
      return "Pipeline";
    default:
      return assertNever(view, "map view");
  }
}

const VIEW_OPTIONS: ReadonlyArray<SegmentedOption<MapView>> = MAP_VIEWS.map((view) => ({
  value: view,
  label: viewLabel(view),
}));

const SCOPES: ReadonlyArray<SegmentedOption<ReportScope>> = [
  { value: "direct", label: "This company" },
  { value: "rolled_up", label: "Including owned companies" },
];

export function MapPage(): ReactNode {
  const search = useSearch({ from: "/map" });
  const navigate = useNavigate();
  const state = useMemo(() => searchToMapState(search), [search]);
  const currency = useReportCurrency();

  const setState = useCallback(
    (next: MapState) => {
      void navigate({ to: "/map", search: mapStateToSearch(next) as MapSearch });
    },
    [navigate],
  );

  usePageChrome({ title: "Map" });

  const scope = { partnerId: state.partnerId, scope: state.scope };
  const deals = useMapDeals(scope);
  const rates = useFxRates();
  const partners = usePartnerOptions();
  const unlocated = useMapLocationGap(scope, "unlocated");
  const noCoordinates = useMapLocationGap(scope, "no_coordinates");

  const today = useMemo(() => todayIsoDate(), []);
  const mapDeals = useMemo(
    () =>
      toMapDeals(deals.data?.items ?? [], currencyView(currency, rates.data?.items ?? []), today),
    [deals.data, rates.data, currency, today],
  );
  // Totals wait for the rates as well as the deals: a total summed before the
  // rates arrive would count every foreign-currency deal as unconvertible.
  const summary =
    deals.data === undefined || rates.data === undefined ? null : summariseMap(mapDeals);

  return (
    <div className={styles.page}>
      <HeadlineTiles summary={summary} currency={currency} />

      <div className={styles.toolbar}>
        <Segmented
          label="Show"
          options={VIEW_OPTIONS}
          value={state.view}
          onChange={(view) => setState({ ...state, view })}
        />
        <PartnerFilter
          partners={partners.data?.items ?? []}
          partnerId={state.partnerId}
          onChange={(partnerId) => setState({ ...state, partnerId })}
        />
        {state.partnerId !== null && (
          <Segmented
            label="Partner scope"
            variant="inset"
            options={SCOPES}
            value={state.scope}
            onChange={(next) => setState({ ...state, scope: next })}
          />
        )}
      </div>

      <Card>
        <CardHeader
          title="Deals by location"
          description="Hover a deal for its details; click to keep the card open."
        />
        <QueryBoundary query={deals} label="the deals on the map">
          {() => (
            <Suspense fallback={<Spinner label="Loading the map…" />}>
              <DealMap deals={mapDeals} view={state.view} />
            </Suspense>
          )}
        </QueryBoundary>
      </Card>

      <Footnotes
        deals={mapDeals}
        total={deals.data?.total ?? null}
        unlocated={unlocated.data?.total ?? null}
        noCoordinates={noCoordinates.data?.total ?? null}
      />
    </div>
  );
}

function HeadlineTiles({
  summary,
  currency,
}: {
  summary: MapSummary | null;
  currency: Currency;
}): ReactNode {
  const money = (minor: number): string => formatMoney(minor, currency);
  return (
    <StatTileRow>
      <StatTile
        label="Revenue billed"
        value={summary === null ? null : money(summary.revenue.billedMinor)}
        hint={
          summary === null
            ? undefined
            : `Billed to date across ${formatCount(summary.revenue.dealCount)} contracted deal${summary.revenue.dealCount === 1 ? "" : "s"}${unconvertedNote(summary.revenue.unconvertible)}.`
        }
      />
      <StatTile
        label="Customers"
        value={summary === null ? null : formatCount(summary.revenue.customerCount)}
        hint="Won, implementing or live, on the map."
      />
      <StatTile
        label="Pipeline deals"
        value={summary === null ? null : formatCount(summary.pipeline.dealCount)}
        hint="Lead through decision maker bought in, on the map."
      />
      <StatTile
        label="Known pipeline value"
        value={summary === null ? null : money(summary.pipeline.knownValueMinor)}
        hint={
          summary === null
            ? undefined
            : `${formatCount(summary.pipeline.pricedCount)} of ${formatCount(summary.pipeline.dealCount)} pipeline deals have a contract value so far${unconvertedNote(summary.pipeline.unconvertible)}.`
        }
      />
    </StatTileRow>
  );
}

function unconvertedNote(count: number): string {
  return count === 0 ? "" : `; ${formatCount(count)} left out with no FX rate to convert them`;
}

function Footnotes({
  deals,
  total,
  unlocated,
  noCoordinates,
}: {
  deals: readonly MapDeal[];
  total: number | null;
  unlocated: number | null;
  noCoordinates: number | null;
}): ReactNode {
  const rate = appliedRate(deals);
  const overLimit = total !== null && total > MAP_DEAL_LIMIT;
  const gaps = (unlocated ?? 0) + (noCoordinates ?? 0);

  if (rate === null && !overLimit && gaps === 0) return null;

  return (
    <div className={styles.footnotes}>
      {rate !== null && (
        <p>
          ≈ Amounts in the other currency are converted at USD→CAD {formatRate(rate.usd_to_cad)},
          the rate in force {formatCalendarDate(rate.as_of)}.
        </p>
      )}
      {overLimit && (
        <p>
          Showing the first {formatCount(MAP_DEAL_LIMIT)} of {formatCount(total)} deals with a
          location.
        </p>
      )}
      {gaps > 0 && (
        <p>
          {formatCount(gaps)} deal{gaps === 1 ? " is" : "s are"} not on the map:{" "}
          {unlocated !== null && unlocated > 0 && (
            <Link to="/deals" search={{ location: "unlocated" }}>
              {formatCount(unlocated)} with no location
            </Link>
          )}
          {unlocated !== null &&
            unlocated > 0 &&
            noCoordinates !== null &&
            noCoordinates > 0 &&
            " · "}
          {noCoordinates !== null && noCoordinates > 0 && (
            <Link to="/deals" search={{ location: "no_coordinates" }}>
              {formatCount(noCoordinates)} with a country but no coordinates
            </Link>
          )}
        </p>
      )}
    </div>
  );
}

/** The rate behind the converted figures on screen, or null when none were. */
function appliedRate(deals: readonly MapDeal[]): FxApplied | null {
  for (const deal of deals) {
    for (const amount of [deal.billed, deal.contracted]) {
      if (amount?.kind === "converted") return amount.rate;
    }
  }
  return null;
}

function PartnerFilter({
  partners,
  partnerId,
  onChange,
}: {
  partners: readonly Partner[];
  partnerId: string | null;
  onChange: (partnerId: string | null) => void;
}): ReactNode {
  return (
    <select
      className={styles.control}
      aria-label="Partner"
      value={partnerId ?? ANY_PARTNER}
      onChange={(event) => {
        const picked = event.target.value;
        onChange(picked === ANY_PARTNER ? null : picked);
      }}
    >
      <option value={ANY_PARTNER}>Every partner</option>
      {partners.map((partner) => (
        <option key={partner.id} value={partner.id}>
          {partner.name}
        </option>
      ))}
    </select>
  );
}
